import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppShellProvider } from '@inform-appshell/mui-appshell';
import {
  getRadiusStyles,
  getSpacingStyles,
  getTokenStyles,
} from '@inform-groundstar-shared-platform/shared-design-system';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { icons } from '../theme/icons';
import { shellTheme } from '../theme/muiTheme';
import interLatin400 from '@fontsource/inter/files/inter-latin-400-normal.woff2';
import interLatin500 from '@fontsource/inter/files/inter-latin-500-normal.woff2';
import interLatin600 from '@fontsource/inter/files/inter-latin-600-normal.woff2';
import interLatin700 from '@fontsource/inter/files/inter-latin-700-normal.woff2';
import monoLatin400 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-400-normal.woff2';
import monoLatin500 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-500-normal.woff2';
import monoLatin600 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-600-normal.woff2';
import monoLatin700 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-700-normal.woff2';

// GB30 names Inter and JetBrains Mono and supplies neither, so both are registered here.
// Through emotion rather than the `@fontsource` CSS, matching the webapp — see ADR-0023.
const face = (fontFamily: string) => (src: string, fontWeight: number) => ({
  fontFamily,
  fontStyle: 'normal',
  fontDisplay: 'swap' as const,
  fontWeight,
  src: `url(${src}) format('woff2')`,
});

const inter = face('Inter');
const mono = face('JetBrains Mono');

const fontFaces = [
  inter(interLatin400, 400),
  inter(interLatin500, 500),
  inter(interLatin600, 600),
  inter(interLatin700, 700),
  mono(monoLatin400, 400),
  mono(monoLatin500, 500),
  mono(monoLatin600, 600),
  mono(monoLatin700, 700),
];

/**
 * GB30's design tokens as CSS custom properties.
 *
 * Its component overrides reference `var(--spacing-*)`, `var(--radius-*)` and `var(--color-*)`,
 * which MUI's `cssVariables` mode does not emit — it emits only `--palette-*`. Without this block
 * every padding and radius resolves to nothing and MUI renders visibly unstyled.
 *
 * Merged, not spread: all three helpers return their own `':root'` key, so the
 * `{ ...getTokenStyles(), ...getSpacingStyles() }` form in GB30's docs discards the first one's
 * variables. `getTokenStyles()` also emits the `[data-mui-color-scheme="dark"]` block.
 */
const tokenStyles = getTokenStyles();
const gb30Tokens = {
  ...tokenStyles,
  ':root': {
    ...tokenStyles[':root'],
    ...getSpacingStyles()[':root'],
    ...getRadiusStyles()[':root'],
  },
};

const cache = createCache({ key: 'shell' });

// Hoisted: AppShellProvider merges on identity, so a fresh literal re-renders every consumer.
// All six are supplied — an omitted glyph falls back to a Material Symbols ligature, which CUAM
// ships no font for and which therefore renders as literal text.
const chromeIcons = {
  navCollapse: <FontAwesomeIcon icon={icons.chevronLeft} />,
  navExpand: <FontAwesomeIcon icon={icons.chevronRight} />,
  navMenu: <FontAwesomeIcon icon={icons.bars} />,
  themeLight: <FontAwesomeIcon icon={icons.sun} />,
  themeDark: <FontAwesomeIcon icon={icons.moon} />,
  warning: <FontAwesomeIcon icon={icons.warning} />,
};

/**
 * The single provider tree for every shell React root, including the pre-`<App/>` renders in
 * `main.tsx`.
 */
export function ShellProviders({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  // The chrome's own strings. `main.tsx` renders this above i18n's own provider, so missing
  // resources resolve to the key rather than throwing.
  const localeText = useMemo(
    () => ({
      navMain: t('navigation.main'),
      navToggle: t('navigation.toggle'),
      themeToLight: t('theme.switchToLight'),
      themeToDark: t('theme.switchToDark'),
      loading: t('common.loading'),
    }),
    [t]
  );

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={shellTheme} defaultMode="system" modeStorageKey="cuam-theme">
        <GlobalStyles styles={{ '@font-face': fontFaces, ...gb30Tokens }} />
        {/* Unscoped, unlike the webapp's: the shell owns its document (ADR-0023). */}
        <CssBaseline />
        <AppShellProvider icons={chromeIcons} localeText={localeText}>
          {children}
        </AppShellProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}
